import { Link } from "wouter";
import { BRAND, PRIMARY_NAV } from "@/lib/content";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
        isScrolled
          ? "bg-background/80 backdrop-blur-md border-border shadow-sm"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Link href="/" className="flex items-center gap-2 relative z-10">
            <img
              src={`${import.meta.env.BASE_URL}brand/logo-lockup-white.svg`}
              alt={BRAND.name}
              className="h-10 w-auto hidden dark:block"
            />
            <img
              src={`${import.meta.env.BASE_URL}brand/logo-lockup.svg`}
              alt={BRAND.name}
              className="h-10 w-auto dark:hidden"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center">
            <NavigationMenu>
              <NavigationMenuList>
                {PRIMARY_NAV.map((item) => (
                  <NavigationMenuItem key={item.label}>
                    {item.children ? (
                      <>
                        <NavigationMenuTrigger className="bg-transparent hover:bg-muted/50 data-[state=open]:bg-muted/50">
                          {item.label}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <div className="w-[400px] p-4 md:w-[500px] lg:w-[600px] grid grid-cols-2 gap-4">
                            {item.children.map((child) => (
                              <Link key={child.label} href={child.path || "#"}>
                                <div className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-muted hover:text-accent-foreground focus:bg-muted focus:text-accent-foreground group cursor-pointer">
                                  <div className="text-sm font-medium leading-none text-foreground group-hover:text-primary transition-colors">
                                    {child.label}
                                  </div>
                                  {child.description && (
                                    <p className="line-clamp-2 text-sm leading-snug text-muted-foreground mt-1.5">
                                      {child.description}
                                    </p>
                                  )}
                                </div>
                              </Link>
                            ))}
                          </div>
                        </NavigationMenuContent>
                      </>
                    ) : (
                      <NavigationMenuLink asChild>
                        <Link
                          href={item.path || "#"}
                          className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-muted hover:text-accent-foreground focus:bg-muted focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-muted/50 data-[state=open]:bg-muted/50 cursor-pointer"
                        >
                          {item.label}
                        </Link>
                      </NavigationMenuLink>
                    )}
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          </div>
        </div>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-4">
          <a href="/app/login" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Sign in
          </a>
          <Link href="/demo">
            <Button className="bg-brand-gradient text-white border-0 hover:opacity-90 transition-opacity no-default-hover-elevate">
              Book a demo
            </Button>
          </Link>
        </div>

        {/* Mobile Menu */}
        <div className="md:hidden">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] overflow-y-auto">
              <div className="flex flex-col gap-6 pt-6">
                {PRIMARY_NAV.map((item) => (
                  <div key={item.label} className="flex flex-col gap-2">
                    {item.children ? (
                      <>
                        <div className="text-sm font-semibold text-foreground/70 uppercase tracking-wider mb-1">
                          {item.label}
                        </div>
                        <div className="flex flex-col gap-3 pl-2 border-l-2 border-muted">
                          {item.children.map((child) => (
                            <Link 
                              key={child.label} 
                              href={child.path || "#"}
                              onClick={() => setMobileMenuOpen(false)}
                              className="text-foreground font-medium hover:text-primary transition-colors block"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </>
                    ) : (
                      <Link 
                        href={item.path || "#"}
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-lg font-semibold hover:text-primary transition-colors block"
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
                <div className="h-px bg-border my-2" />
                <div className="flex flex-col gap-4">
                  <a href="/app/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full justify-center">Sign in</Button>
                  </a>
                  <Link href="/demo" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full justify-center bg-brand-gradient text-white border-0">
                      Book a demo
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
